import React, { useState } from 'react';
import { CustomField } from '../../types/configTemplate';
import { CustomFieldModal } from './CustomFieldModal';
import { useI18n } from '../../contexts/I18nContext';
import './DeviceSpecificFields.css';

interface DeviceSpecificFieldsProps {
  fields: CustomField[];
  onChange: (fields: CustomField[]) => void;
  isEditing: boolean;  // View or edit mode
}

export const DeviceSpecificFields: React.FC<DeviceSpecificFieldsProps> = ({
  fields,
  onChange,
  isEditing
}) => {
  const { t } = useI18n();
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingField, setEditingField] = useState<CustomField | null>(null);

  const handleAddField = (field: CustomField) => {
    onChange([...fields, field]);
  };

  const handleEditField = (field: CustomField) => {
    onChange(fields.map(f => f.name === field.name ? field : f));
  };

  const handleDeleteField = (name: string) => {
    onChange(fields.filter(f => f.name !== name));
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditingField(null);
  };

  const handleModalSave = (field: CustomField) => {
    if (editingField) {
      handleEditField(field);
    } else {
      handleAddField(field);
    }
  };

  const existingNames = fields.map(f => f.name);

  return (
    <div className="device-specific-fields">
      <div className="section-header">
        <h4>{t('hardwareDefault.deviceSpecificFields')}</h4>
        {isEditing && (
          <button
            className="btn btn-sm btn-primary"
            onClick={() => setShowAddModal(true)}
            type="button"
          >
            + {t('deviceTypes.addField')}
          </button>
        )}
      </div>

      {fields.length === 0 ? (
        <div className="empty-state">
          {t('hardwareDefault.deviceSpecificFields')}: —
        </div>
      ) : (
        <table className="custom-fields-table">
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              <th>{t('common.value')}</th>
              <th>{t('common.source')}</th>
              <th>{t('common.group')}</th>
              <th>{t('common.notes')}</th>
              {isEditing && <th>{t('common.actions')}</th>}
            </tr>
          </thead>
          <tbody>
            {fields.map(field => (
              <tr key={field.name}>
                <td className="field-name">{field.name}</td>
                <td className="field-value" title={String(field.value)}>
                  {field.value != null ? String(field.value) : '-'}
                  {field.default !== undefined && field.value !== field.default && (
                    <span className="field-default-hint" title={`Default: ${field.default}`}>
                      (default: {String(field.default)})
                    </span>
                  )}
                </td>
                <td className="field-source">
                  {field.source === 'device_type' && (
                    <span className="source-badge source-badge-default">{t('common.default')}</span>
                  )}
                  {field.source === 'hardware_field_def' && (
                    <span className="source-badge source-badge-default">{t('hardwareDefault.badge')}</span>
                  )}
                  {field.source === 'template' && (
                    <span className="source-badge source-badge-template">{t('configSources.layers.template')}</span>
                  )}
                  {field.source === 'device' && (
                    <span className="source-badge source-badge-override">{t('configSources.override')}</span>
                  )}
                  {!field.source && <span>-</span>}
                </td>
                <td className="field-group">{field.group || '-'}</td>
                <td className="field-notes" title={field.notes}>{field.notes || '-'}</td>
                {isEditing && (
                  <td className="field-actions">
                    <button
                      className="btn-icon"
                      onClick={() => setEditingField(field)}
                      title={t('common.edit')}
                      type="button"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                    <button
                      className="btn-icon btn-danger"
                      onClick={() => handleDeleteField(field.name)}
                      title={t('common.delete')}
                      type="button"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <CustomFieldModal
        isOpen={showAddModal || editingField !== null}
        field={editingField}
        onClose={handleModalClose}
        onSave={handleModalSave}
        existingNames={existingNames}
      />
    </div>
  );
};
